import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
// Components
import LoadingScreen from './components/utils/LoadingScreen';
// Utils
import { AuthenticateAdmin } from './utils/user/AuthenticateUser';
// Constants
import {
  CONTACT_PAGE_URL,
  HOME_PAGE_URL,
  LOGIN_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  POLICIES_PAGE_URL,
  RESET_PASS_PAGE_URL,
  BOOKING_PAGE_URL,
  BOOKING_ADMIN_PAGE_URL,
  CONTACT_ADMIN_PAGE_URL,
  ABOUT_PAGE_URL,
  COMMERCIALS_PAGE_URL,
  WELLNESS_PAGE_URL,
} from './utils/Routes';
import { COOKIE_TIMER, CookiePolicyName } from './utils/Constants';
// Pages
// Public
import HomePage from './pages/home/HomePage';
import ContactPage from './pages/contact/ContactPage';
// Booking
import BookingPage from './pages/booking/BookingPage';
import BookingAdminPage from './pages/booking/admin/BookingAdminPage';
import ConfirmBookingAutoPage from './pages/booking/admin/ConfirmBookingAutoPage';
import DenyBookingAutoPage from './pages/booking/admin/DenyBookingAutoPage';
// User pages
import LoginPage from './users/login/LoginPage';
import ForgettenPasswordPage from './users/password/ForgettenPasswordPage';
// Terms and conditions
import TermAndPoliciesPage from './pages/policies/TermAndPoliciesPage';
// Maintainance
import MaintenancePage from './pages/maintenance/MaintenancePage';
// Error
import Error404 from './pages/error/Error404';
import useScrollToTopOnRouteChange from './utils/functions/ScrollToTop';
import ContactFormAdminPage from './pages/contact/admin/ContactFormAdminPage';
import AboutPage from './pages/about/AboutPage';
import CommercialsPage from './pages/commercials/CommercialsPage';
import WellnessPage from './pages/wellness/WellnessPage';

// Components
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreedToCookies, setHasAgreedToCookies] = useState(true);

  useScrollToTopOnRouteChange();

  useEffect(() => {
    const cookie = localStorage.getItem(CookiePolicyName);

    if (cookie) {
      setHasAgreedToCookies(true);
    } else {
      setHasAgreedToCookies(false);
    }
  }, []);

  useEffect(() => {
    if (hasAgreedToCookies) {
      setIsVisible(false);
    }

    const timer = setTimeout(() => {
      if (!hasAgreedToCookies) {
        setIsVisible(true);
      }
    }, COOKIE_TIMER);

    return () => clearTimeout(timer);
  }, [hasAgreedToCookies]);

  return (
    <>
      {isVisible && (
        <Suspense>
          <CookieConsentModal setHasAgreedToCookies={setHasAgreedToCookies} />
        </Suspense>
      )}

      {/* Suspense component wraps the lazy-loaded components */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Main page routes */}
          <Route path={HOME_PAGE_URL} index element={<HomePage />} />
          <Route path={ABOUT_PAGE_URL} element={<AboutPage />} />
          <Route path={COMMERCIALS_PAGE_URL} element={<CommercialsPage />} />
          <Route path={WELLNESS_PAGE_URL} element={<WellnessPage />} />

          {/* Booking routes */}
          <Route path={BOOKING_PAGE_URL} element={<BookingPage />} />
          <Route path={BOOKING_ADMIN_PAGE_URL} element={<BookingAdminPage />} />
          <Route
            path={`/bookings/confirm-booking/:uniqueString/:bookingId`}
            element={<ConfirmBookingAutoPage />}
          />
          <Route
            path={`/bookings/deny-booking/:uniqueString/:bookingId`}
            element={<DenyBookingAutoPage />}
          />

          {/* Contact routes */}
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />
          <Route path={CONTACT_ADMIN_PAGE_URL} element={<ContactFormAdminPage />} />

          {/* User routes */}
          <Route path={LOGIN_PAGE_URL} element={<LoginPage />} />
          <Route
            path={RESET_PASS_PAGE_URL}
            element={<ForgettenPasswordPage />}
          />

          {/* Terms and conditions */}
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />

          {/* Other */}
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />

          {/* Error routes */}
          <Route path='*' element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
